import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { green, grey, orange, red } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isNil } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import fpSimulationService from "@plannel/services/fp/fp-simulation-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SimulationOptionPopup from "@plannel/pages/factory-plan/common/popup/SimulationOptionPopup";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// 공통코드 그룹 FP_EXEC_TYPE의 두 코드. PLAN = 엔진 계획 실행(플랜 버전 채번 + 옵션 적용 가능),
// PROCEDURE = DB 저장 프로시저 실행(옵션/시나리오 없음). old의 분기 기준과 동일하다.
const EXEC_TP_PLAN = "FP_EXEC_PLAN";
const EXEC_TP_PROCEDURE = "FP_EXEC_PROCEDURE";

const STATUS_READY = "FP_STATUS_READY";
const STATUS_RUNNING = "FP_STATUS_RUNNING";
const STATUS_COMPLETE = "FP_STATUS_COMPLETE";
const STATUS_FAIL = "FP_STATUS_FAIL";

// old 상태 렌더러의 색/아이콘 대응(회색 pause / 주황 회전 / 초록 체크 / 빨강 X).
// old도 이 4개 외의 상태(FP_STATUS_CONFIRM 등)에는 뱃지를 그리지 않고 텍스트만 보여준다.
const STATUS_BADGES = {
  [STATUS_READY]: { color: grey[600], Icon: PauseIcon },
  [STATUS_RUNNING]: { color: orange[700], Icon: null },
  [STATUS_COMPLETE]: { color: green[600], Icon: CheckIcon },
  [STATUS_FAIL]: { color: red[500], Icon: CloseIcon }
};

// 좌측 버전 생성 패널 폭 — old Grid item(width 35%, minWidth 607px) 대응.
const VERSION_PANEL_WIDTH = 620;
// 버전 생성 폼: 라벨 열 고정폭 + 입력 폭(old 테이블의 28% 라벨 열 대응).
const VERSION_LABEL_WIDTH = 130;
const VERSION_INPUT_WIDTH = 190;
// 상단 영역(버전 생성 + 상세) 높이 — old minHeight 362px 대응. 하단 이력이 나머지를 가져간다.
const TOP_AREA_HEIGHT = 366;
const HISTORY_MIN_HEIGHT = 220;
// 좌/우 패널의 첫 줄이 같은 baseline에 오도록 헤더 높이를 상수로 고정(SimulationStep과 동일 규칙).
const PANEL_HEADER_HEIGHT = 52;
// old checkEngineStatus의 재확인 주기(3초).
const ENGINE_POLL_INTERVAL_MS = 3000;
// old는 실행 POST를 던지고 1초 뒤 상세를 다시 읽어 뱃지를 진행중으로 바꿨다(SimulationDetails.jsx:524).
// 3초 폴링만 기다리면 첫 전환이 늦어 그 동작을 그대로 복원한다.
const FIRST_STATUS_SYNC_DELAY_MS = 1000;
// 엔진 슬롯 해제(FpEngineService:99 finally release)가 web JVM의 COMPLETE 기록(ExampleService:178)보다
// 먼저다. 슬롯이 비었다고 곧바로 끝내면 아직 RUNNING을 읽으므로 정리될 때까지 몇 틱 더 보되,
// 웹 JVM이 죽어 상태가 영영 안 바뀌는 경우를 대비해 상한을 둔다.
const ENGINE_SETTLE_MAX_TICKS = 3;
// RUNNING은 엔진 호출 직전에 DB에 기록되므로 몇 초 안에 보인다. 그 안에 안 보이면 지금 도는 실행이
// 이 화면의 메인 버전과 무관한 것(남이 다른 버전을 돌리는 중)이라 더 읽어도 소용없다.
const STATUS_CATCHUP_MAX_TICKS = 5;

// 백그라운드 갱신이 덮어써도 되는 읽기전용 필드 / 다시 그릴 컬럼.
// 편집 컬럼(policyCd, versionDescTxt)은 절대 넣지 않는다 — 사용자가 방금 고른 값이 날아간다.
const STATUS_SYNC_FIELDS = ["statusTpCd", "startTs", "endTs", "versionCd"];
const STATUS_SYNC_COLUMNS = ["statusTpCd", "startTs", "endTs", "elapsedTime", "versionCd"];

const isBlank = v => isNil(v) || v === "";

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return dateUtils.dateValidation(parsed) ? parsed : null;
};

// 백엔드 LocalDateTime이 받는 ISO 문자열로. old convertDateFormat('yyyy-MM-ddTHH:mm:00')과 동일하게 초는 버린다.
const toTimestamp = (value) => (value ? dateUtils.formatDate(value, "yyyy-MM-dd'T'HH:mm:00") : null);

// old elapsedTime valueCallback — 시작/종료가 모두 있을 때만 "Nm Ns".
const elapsedTimeGetter = (params) => {
  const start = toDate(params.data?.startTs);
  const end = toDate(params.data?.endTs);
  if (!start || !end) return null;

  const elapsedSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
  if (elapsedSeconds < 0) return null;

  return `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s`;
};

// 상태 뱃지(원형 아이콘) + 상태명. old의 html 렌더러 대응.
const StatusRenderer = ({ value, t }) => {
  if (isBlank(value)) return "";

  const badge = STATUS_BADGES[value];

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, height: "100%" }}>
      {badge && (
        <Box sx={{
          width: 20, height: 20, borderRadius: "50%", bgcolor: badge.color,
          display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto"
        }}>
          {badge.Icon
            ? <badge.Icon sx={{ fontSize: 14, color: "#fff" }} />
            : <CircularProgress size={12} sx={{ color: "#fff" }} />}
        </Box>
      )}
      <Box component="span">{t(value)}</Box>
    </Box>
  );
};

// 실행/옵션 버튼 셀. old execButton 렌더러 대응 — 옵션 버튼은 실행유형이 PLAN인 행에만 나온다.
const ExecActionRenderer = ({ data, node, t, onExec, onOption, disabled }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
    <Button size="small" variant="outlined" disabled={disabled} onClick={() => onExec(node)} sx={{ minWidth: 56, py: 0 }}>
      {t("EXEC")}
    </Button>
    {data?.execTpCd === EXEC_TP_PLAN && (
      <Button size="small" variant="outlined" color="inherit" disabled={disabled} onClick={() => onOption(node)} sx={{ minWidth: 56, py: 0 }}>
        {t("option")}
      </Button>
    )}
  </Box>
);

// 이력의 옵션 아이콘 — 저장된 옵션이 있는 행에만 표시되고 누르면 옵션을 읽기 전용으로 연다.
const OptionIconRenderer = ({ data, onOpen }) => {
  if (data?.hasOptions !== "Y") return "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <IconButton size="small" color="primary" onClick={() => onOpen(data)}>
        <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

/**
 * 버전 생성 폼의 한 줄 — 좌측 불릿+라벨, 우측 입력. old의 label/input 2열 테이블 대응.
 * 입력은 전부 `variant="standard"`(밑줄형)로 맞춘다.
 */
const VersionField = ({ label, last, children }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 1, minHeight: 52,
    borderBottom: last ? 0 : 1, borderColor: "divider"
  }}>
    <Box sx={{ width: VERSION_LABEL_WIDTH, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "text.secondary", flex: "0 0 auto" }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>{children}</Box>
  </Box>
);

const ConfirmRenderer = ({ value }) => {
  if (value !== true) return "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <Box sx={{
        width: 20, height: 20, borderRadius: "50%", bgcolor: green[600],
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />
      </Box>
    </Box>
  );
};

const Simulation = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");

  // ── 좌상: 메인 버전 생성 ───────────────────────────────────────────────
  const [stepList, setStepList] = useState([]);
  // 절차 목록도 비동기 로드라, 곧이어 실행되는 retrieveDefaultMainVersion의 클로저는 아직 옛 stepList를
  // 잡고 있다. 서버가 준 stepCd가 마스터에 있는지 그 시점에 확인해야 해서 ref로도 들고 있는다
  // (policyListRef와 같은 이유 · §0-7).
  const stepListRef = useRef([]);
  const [mainVersionCd, setMainVersionCd] = useState("");
  const [versionForm, setVersionForm] = useState({ descTxt: "", startTs: null, endTs: null, freezeTs: null, stepCd: "", versionSeq: null });
  // 이미 저장된 메인 버전은 편집 불가(old disabled = !!data.id). 신규 생성 시에만 열린다.
  const [versionReadOnly, setVersionReadOnly] = useState(false);
  // 저장된 메인 버전만 상세/실행의 기준이 된다(미저장 신규 버전이면 null → 상세 그리드 비움).
  const [mainVersion, setMainVersion] = useState(null);

  // ── 우상: 실행 절차 상세 ──────────────────────────────────────────────
  const [details, setDetails] = useState([]);
  const detailGridRef = useRef();
  // 시나리오(계획 정책) 목록은 비동기 로드라 컬럼 캡처 시점엔 비어 있다 → ref로 들고 편집 시점에 읽는다(§0-7).
  const policyListRef = useRef([]);
  const defaultPolicyRef = useRef("");

  // ── 하단: 실행 이력 ───────────────────────────────────────────────────
  const [histories, setHistories] = useState([]);
  const historyGridRef = useRef();

  // ── 엔진 상태 ─────────────────────────────────────────────────────────
  const [engineRunning, setEngineRunning] = useState(false);
  const enginePollRef = useRef(null);
  // 이 탭이 던진 실행이 아직 응답 전인가. "엔진이 안 돈다"는 프로브 결과를 언제 믿어도 되는지 가르는
  // 유일한 기준이다(아래 startEnginePolling 주석 참고). 폴링 콜백에서 읽으므로 state가 아니라 ref다.
  const execInFlightRef = useRef(false);
  // 상세 그리드에 RUNNING이 이미 보이는가. 보이면 실행이 끝날 때까지 상세를 더 읽지 않는다.
  const runningVisibleRef = useRef(false);
  // old의 "실행 1초 뒤 중간 재조회" 타이머.
  const firstSyncTimerRef = useRef(null);
  // 엔진이 멈춘 뒤 상태가 COMPLETE로 정리되길 기다린 틱 수.
  const settleTicksRef = useRef(0);
  // RUNNING이 보이길 기다리며 상세를 다시 읽은 틱 수.
  const catchUpTicksRef = useRef(0);

  const [optionPopup, setOptionPopup] = useState({ open: false, title: "", versionCd: null });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);

  // 확인 다이얼로그를 거쳐야 실행되므로 대상 행/옵션을 잠시 보관한다(렌더에 관여하지 않아 ref).
  const pendingExecRef = useRef(null);

  // 조회가 다단(절차목록·정책·이력 / 상세)이라 요청 토큰 가드도 2개다(fp-frontend.md §0-8).
  const latestScopeReqRef = useRef(0);
  const latestDetailReqRef = useRef(0);
  // 최신 mainVersion을 콜백(폴링·실행 후처리)에서 읽어야 해서 ref로도 들고 있는다.
  const mainVersionRef = useRef(null);

  // gridId는 사용자별 컬럼상태(p13n) 저장 키다. 한 화면에 그리드가 2개 이상이면 반드시 다르게 준다 —
  // 기본값이 "grid"라 그냥 두면 두 그리드가 같은 키에 서로 덮어쓰고, 로드는 아래 setColumnDefs가
  // "detail"/"history"로 읽어서 저장한 것을 영영 못 찾는다. setColumnDefs와 **같은 값**이어야 한다.
  const detailGridMemo = useMemo(() => FpGridSetting({ title, viewName, gridId: "detail" }), [title, viewName]);
  const historyGridMemo = useMemo(() => FpGridSetting({ title, viewName, gridId: "history" }), [title, viewName]);

  const stepLabel = useMemo(
    () => stepList.find(step => step.stepCd === mainVersion?.stepCd)?.stepNm ?? "",
    [stepList, mainVersion]);

  const showSnack = (severity, content) => setSnackInfo({ open: true, severity, content });

  // ── 조회 ─────────────────────────────────────────────────────────────

  const buildParams = (extra = {}) => ({ viewId: viewName, subId: "", planScope, ...extra });

  const retrievePlanPolicies = (reqId) => {
    fpCommonService.getPlanPolicies(buildParams())
      .then(response => {
        if (reqId !== latestScopeReqRef.current) return;
        const list = [...(response.data ?? [])];
        // old loadPlanPolicy: 기본 시나리오를 목록 맨 앞으로 끌어올린다.
        const defaultIndex = list.findIndex(policy => policy.defaultYn === true);
        if (defaultIndex > 0) {
          list.unshift(...list.splice(defaultIndex, 1));
        }
        policyListRef.current = list;
        defaultPolicyRef.current = list.find(policy => policy.defaultYn === true)?.policyCd ?? (list[0]?.policyCd ?? "");
      })
      .catch(e => debug(e));
  };

  const retrieveDetails = (targetMainVersionCd) => {
    if (!targetMainVersionCd) {
      latestDetailReqRef.current += 1;
      runningVisibleRef.current = false;
      setDetails([]);
      return;
    }

    const reqId = ++latestDetailReqRef.current;

    detailGridRef.current?.api?.setGridOption("loading", true);

    fpSimulationService.getDetails(buildParams({ mainVersionCd: targetMainVersionCd }))
      .then(response => {
        if (reqId !== latestDetailReqRef.current) return;
        // 상세를 읽을 때마다 "RUNNING이 보이는가"를 다시 판정한다(단방향 래치가 아니다) —
        // 남이 시작한 새 실행도 자동으로 다시 따라잡게 된다.
        runningVisibleRef.current = (response.data ?? []).some(row => row.statusTpCd === STATUS_RUNNING);
        // old loadData 후처리: 설명이 비면 메인 버전 설명으로, PLAN 행의 시나리오가 비면 기본 시나리오로 채운다.
        const rows = (response.data ?? []).map(row => ({
          ...row,
          versionDescTxt: isBlank(row.versionDescTxt) ? (mainVersionRef.current?.descTxt ?? "") : row.versionDescTxt,
          policyCd: (row.execTpCd === EXEC_TP_PLAN && isBlank(row.policyCd)) ? defaultPolicyRef.current : row.policyCd
        }));
        setDetails(rows);
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestDetailReqRef.current) return;
        DataState.initialize(detailGridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestDetailReqRef.current) detailGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveHistories = (reqId) => {
    const token = isNil(reqId) ? latestScopeReqRef.current : reqId;

    historyGridRef.current?.api?.setGridOption("loading", true);

    fpSimulationService.getHistories(buildParams())
      .then(response => {
        if (token !== latestScopeReqRef.current) return;
        setHistories(response.data ?? []);
      })
      .catch(e => debug(e))
      .finally(() => {
        if (token === latestScopeReqRef.current) historyGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveDefaultMainVersion = (newMainVersion, reqId) => {
    fpSimulationService.getDefaultMainVersion(buildParams({ newMainVersion }))
      .then(response => {
        if (reqId !== latestScopeReqRef.current) return;

        const data = response.data;
        if (!data) {
          setMainVersionCd("");
          setVersionReadOnly(false);
          setMainVersion(null);
          return;
        }

        // 서버는 절차 마스터를 조회하지 않고 stepCd를 하드코딩해서 내려준다(SimulationService:105 —
        // old SimulationService:98과 동일). tb_fp_plan_step에 없는 코드를 그대로 폼에 넣으면 화면에
        // 원시 코드가 노출되고 저장까지 된다(tb_fp_main_version.step_cd에 FK가 없어 DB도 안 막는다).
        // 마스터에 없으면 미선택으로 두고 사용자가 실제 절차를 고르게 한다.
        const serverStepCd = data.stepCd ?? "";
        const stepCd = stepListRef.current.some(step => step.stepCd === serverStepCd) ? serverStepCd : "";

        setMainVersionCd(data.mainVersionCd ?? "");
        setVersionForm({
          descTxt: data.descTxt ?? "",
          startTs: toDate(data.startTs),
          endTs: toDate(data.endTs),
          freezeTs: toDate(data.freezeTs),
          stepCd,
          versionSeq: data.versionSeq ?? null
        });

        // id가 있으면 이미 저장된 버전 → 읽기 전용 + 상세 조회 기준. 없으면 미저장 신규 버전.
        const saved = !isNil(data.id);
        setVersionReadOnly(saved);
        setMainVersion(saved ? data : null);
      })
      .catch(e => debug(e));
  };

  const retrievePlanSteps = (reqId) => {
    fpCommonService.getPlanSteps(buildParams())
      .then(response => {
        if (reqId !== latestScopeReqRef.current) return;
        stepListRef.current = response.data ?? [];
        setStepList(stepListRef.current);
        // old도 절차 목록이 채워진 뒤에 기본 메인 버전을 읽는다(절차 코드 → 명칭 표시에 필요).
        retrieveDefaultMainVersion(false, reqId);
      })
      .catch(e => debug(e));
  };

  // ── 엔진 상태 ────────────────────────────────────────────────────────

  // 프로브 실패는 "안 돌고 있음"이 아니라 "모름"(null)이다. false로 접으면 네트워크가 한 번만 끊겨도
  // 실행중 표시가 사라지고 실행 버튼이 다시 열린다. 호출부가 true/false/null을 구분해서 쓴다.
  const fetchEngineRunning = () =>
    fpSimulationService.isEngineRunning(buildParams({ allPlanScope: false }))
      .then(response => response.data === true)
      .catch(e => {
        debug(e);
        return null;
      });

  /**
   * 엔진 실행 중 상태 뱃지만 조용히 따라잡는다(대기 → 진행중).
   *
   * retrieveDetails를 재사용하지 않는 이유:
   *  1) rowData를 갈아끼우면 getRowId 미지정이라 행 노드가 재생성돼 편집 중인 시나리오 값·
   *     DataState 추적·포커스·스크롤이 전부 날아간다.
   *  2) setGridOption("loading")이 폴링 주기마다 번쩍인다.
   *  3) node.data는 details 배열의 원소와 같은 객체라, 제자리에서 고치면 state도 함께 최신이 된다.
   *
   * 요청 토큰(latestDetailReqRef)은 올리지 않고 읽기만 한다 — 배경 갱신이 사용자 조회를 취소시키면 안 된다.
   */
  const syncDetailStatuses = () => {
    const targetMainVersionCd = mainVersionRef.current?.mainVersionCd;
    if (!targetMainVersionCd) return;

    const reqId = latestDetailReqRef.current;

    fpSimulationService.getDetails(buildParams({ mainVersionCd: targetMainVersionCd }))
      .then(response => {
        // 그 사이 메인 버전·계획범위가 바뀌었으면 버린다.
        if (reqId !== latestDetailReqRef.current) return;

        const rows = response.data ?? [];
        runningVisibleRef.current = rows.some(row => row.statusTpCd === STATUS_RUNNING);

        const api = detailGridRef.current?.api;
        if (!api || api.isDestroyed()) return;

        const rowByStep = new Map(rows.map(row => [`${row.stepCd}#${row.stepSeq}`, row]));
        const touched = [];

        api.forEachNode(node => {
          const next = rowByStep.get(`${node.data?.stepCd}#${node.data?.stepSeq}`);
          if (!next) return;
          STATUS_SYNC_FIELDS.forEach(field => { node.data[field] = next[field]; });
          touched.push(node);
        });

        if (touched.length > 0) {
          api.refreshCells({ rowNodes: touched, columns: STATUS_SYNC_COLUMNS, force: true });
        }
      })
      .catch(e => debug(e));
  };

  const stopEnginePolling = () => {
    if (enginePollRef.current) {
      clearInterval(enginePollRef.current);
      enginePollRef.current = null;
    }
    // 중간 재조회 타이머도 같은 자리에서 정리한다 — 정리 지점이 흩어지면 언마운트·계획범위 전환에서
    // 하나가 살아남는다.
    if (firstSyncTimerRef.current) {
      clearTimeout(firstSyncTimerRef.current);
      firstSyncTimerRef.current = null;
    }
    settleTicksRef.current = 0;
    catchUpTicksRef.current = 0;
  };

  const startEnginePolling = () => {
    if (enginePollRef.current) return;

    enginePollRef.current = setInterval(() => {
      // (a) 뱃지 따라잡기 — RUNNING이 보일 때까지만, 그것도 몇 틱만.
      //     tb_fp_plan_history의 RUNNING은 엔진 호출 직전 딱 한 번 기록되고(ExampleService.runPlan:172,
      //     프로시저는 SimulationService.createPlanHistoryInProcedure:514) 실행이 끝날 때까지 안 바뀐다.
      //     한 번 본 뒤엔 읽어올 새 정보가 없고, 몇 틱 안에도 안 보이면 남이 다른 메인 버전을 돌리는
      //     중이라 계속 읽어봐야 소용없다. 상세 조회는 무거워서(3-way 병합) 둘 다 막아야 한다.
      if (!runningVisibleRef.current && catchUpTicksRef.current < STATUS_CATCHUP_MAX_TICKS) {
        catchUpTicksRef.current += 1;
        syncDetailStatuses();
      }

      // (b) 종료 판정
      fetchEngineRunning().then(running => {
        // true(도는 중) / null(프로브 실패 = 모름) 둘 다 종료로 보지 않는다.
        if (running !== false) return;

        // 내가 던진 실행이 아직 응답 전이면 false를 믿지 않는다:
        //  · 엔진 슬롯(CAS)은 백엔드가 DB에 RUNNING을 쓴 "뒤"에 잡힌다(ExampleService.runPlan:172 → :174)
        //  · 프로시저 실행은 엔진 서버를 아예 안 거쳐 실행 내내 false다(SimulationService.executeProcedure)
        // 여기서 멈추면 스피너가 사라지고 실행 버튼이 다시 열린다. 내 실행의 끝은 finish()가 알려준다.
        if (execInFlightRef.current) return;

        // 슬롯 해제(FpEngineService:99)가 COMPLETE 기록(ExampleService:178)보다 먼저라 곧바로 읽으면
        // 아직 RUNNING이다. 정리될 때까지 몇 틱만 더 본다.
        if (runningVisibleRef.current && settleTicksRef.current < ENGINE_SETTLE_MAX_TICKS) {
          settleTicksRef.current += 1;
          syncDetailStatuses();
          return;
        }

        stopEnginePolling();
        setEngineRunning(false);
        retrieveDetails(mainVersionRef.current?.mainVersionCd);
        retrieveHistories();
      });
    }, ENGINE_POLL_INTERVAL_MS);
  };

  // TODO(MIG-T2): [simulation/Simulation] 엔진 실행 중 화면 전체 차단(waitMe 오버레이) 미이관.
  //   old: checkEngineStatus가 waitOn(workAreaId)으로 작업영역 전체를 덮고 3초마다 재확인, 끝나면 waitOff.
  //   사유: new의 Backdrop은 짧은 요청용이라 실행 준비 구간(버전 채번·옵션 저장)에만 걸고, 분 단위
  //         엔진 실행 자체는 executeStep의 beginLongRun이 걷어낸다 — 모달이 덮고 있으면 상태 뱃지가
  //         진행중으로 바뀌어도 사용자가 볼 수 없다. 대신 RunMP.js 등 new 엔진 실행 화면 관례대로
  //         헤더 진행 표시 + 실행/옵션 버튼 비활성 + 좌측 메인 버전 패널 잠금으로 대체했다(주기 동일).
  const checkEngineStatus = () => {
    stopEnginePolling();
    fetchEngineRunning().then(running => {
      setEngineRunning(running === true);
      if (running === true) startEnginePolling();
    });
  };

  // ── 저장 · 실행 ──────────────────────────────────────────────────────

  const saveMainVersion = () => {
    const changes = {
      planScope,
      mainVersionCd,
      descTxt: versionForm.descTxt,
      stepCd: versionForm.stepCd,
      versionSeq: versionForm.versionSeq,
      startTs: toTimestamp(versionForm.startTs),
      endTs: toTimestamp(versionForm.endTs),
      freezeTs: toTimestamp(versionForm.freezeTs)
    };

    fpSimulationService.saveMainVersion(buildParams(), changes)
      .then(response => {
        const { message, data } = response.data ?? {};
        // 코드가 중복되면 서버가 새 코드로 재채번하고 existVersion/newVersion을 함께 돌려준다.
        const savedVersionCd = data?.newVersion ?? mainVersionCd;
        showSnack(snackBarMessageColor.saveSuccess,
          message ? t(message, { existVersion: data?.existVersion, newVersion: data?.newVersion }) : t("MSG_SaveSuccess"));

        setMainVersionCd(savedVersionCd);
        setVersionReadOnly(true);
        setMainVersion({ ...changes, mainVersionCd: savedVersionCd });
      })
      .catch(e => {
        debug(e);
        showSnack(snackBarMessageColor.saveFail, t("MSG_SaveFailed"));
      })
      .then(() => {
        setProgressOn(false);
      });
  };

  const createNewMainVersion = () => {
    const reqId = ++latestScopeReqRef.current;
    setDetails([]);
    retrieveDefaultMainVersion(true, reqId);
  };

  const executeStep = (node, options) => {
    const gridApi = detailGridRef.current?.api;
    gridApi?.stopEditing();

    const row = { ...(node?.data ?? {}) };
    const currentMainVersion = mainVersionRef.current;
    // 실행이 분 단위라 그 사이 계획범위가 바뀔 수 있다. 그러면 이 실행의 뒤처리를 새 화면에 적용하면 안 된다
    // (old SimulationDetails.jsx:533,544의 requestPlanScope 가드 대응 — 이관 시 누락).
    // 계획범위 전환은 latestScopeReqRef를 올리므로 그 토큰이 곧 "같은 화면인가" 판정이다(§0-8).
    const execScopeToken = latestScopeReqRef.current;

    if (!currentMainVersion?.mainVersionCd) {
      setProgressOn(false);
      showSnack(snackBarMessageColor.warning, t("MSG_NoData"));
      return;
    }

    // 프로시저 실행은 params.userNm을 프로시저 인자로 그대로 넘긴다(old도 useUserStore의 username 사용).
    const execParams = buildParams({ userNm: JSON.parse(localStorage.getItem("user"))?.username ?? "" });

    // 엔진/프로시저 실행은 분 단위다. 실제 실행 POST를 던지기 직전에 Backdrop을 걷고 "실행중 표시"로
    // 바꾼다 — 모달이 덮고 있으면 상태 뱃지가 진행중으로 바뀌어도 사용자가 볼 수 없다.
    const beginLongRun = () => {
      setProgressOn(false);
      execInFlightRef.current = true;
      runningVisibleRef.current = false;   // 새 RUNNING을 처음부터 다시 따라잡는다
      setEngineRunning(true);              // 헤더 스피너 + 실행/옵션/좌측 패널 버튼 비활성 즉시 반영
      stopEnginePolling();                 // 틱 카운터·타이머를 초기화하고 새 클로저로 다시 건다
      startEnginePolling();
      // old의 "실행 1초 뒤 중간 재조회"(SimulationDetails.jsx:524) — 첫 뱃지 전환을 3초에서 1초로 당긴다.
      firstSyncTimerRef.current = setTimeout(syncDetailStatuses, FIRST_STATUS_SYNC_DELAY_MS);
    };

    const finish = () => {
      // 가드보다 먼저 풀어야 계획범위가 바뀐 뒤에도 새 스코프의 폴링이 정상적으로 종료 판정을 한다.
      execInFlightRef.current = false;
      setProgressOn(false);
      if (execScopeToken !== latestScopeReqRef.current) return;
      retrieveDetails(currentMainVersion.mainVersionCd);
      retrieveHistories();
      checkEngineStatus();
    };

    const fail = (e) => {
      debug(e);
      const message = e?.response?.data?.message;
      showSnack(snackBarMessageColor.saveFail, message ? `${t("FP_MSG_FAIL_EXEC")} - ${t(message)}` : t("FP_MSG_FAIL_EXEC"));
    };

    fetchEngineRunning()
      .then(running => {
        if (running) {
          showSnack(snackBarMessageColor.warning, t("FP_MSG_ENGINE_ALREADY_RUNNING"));
          return null;
        }

        if (row.execTpCd === EXEC_TP_PROCEDURE) {
          beginLongRun();
          return fpSimulationService.executeProcedure(execParams, {
            mainVersionCd: currentMainVersion.mainVersionCd,
            stepCd: row.stepCd,
            stepSeq: row.stepSeq
          }).then(() => showSnack(snackBarMessageColor.saveSuccess, t("FP_MSG_COMPLETE_EXEC")));
        }

        // 계획 실행: 플랜 버전 저장 → (옵션이 있으면) 옵션 저장 → 엔진 실행. old savePlanVersion 흐름과 동일.
        const planVersion = {
          planScope,
          mainVersionCd: currentMainVersion.mainVersionCd,
          startTs: currentMainVersion.startTs,
          endTs: currentMainVersion.endTs,
          freezeTs: currentMainVersion.freezeTs,
          stepCd: row.stepCd,
          stepSeq: row.stepSeq,
          policyCd: row.policyCd,
          descTxt: row.versionDescTxt
        };

        return fpSimulationService.savePlanVersion(execParams, planVersion)
          .then(response => {
            const newVersionCd = response.data?.data?.newVersionCd;
            planVersion.versionCd = newVersionCd;

            if (!options || options.length === 0) return null;

            return fpSimulationService.saveOptions(execParams, options.map(option => ({ ...option, versionCd: newVersionCd })))
              .catch(e => {
                debug(e);
                showSnack(snackBarMessageColor.saveFail, t("FP_MSG_FAIL_OPTION_SAVE"));
                throw e;
              });
          })
          .then(() => {
            // 버전 채번·옵션 저장까지 끝난 뒤에야 장기 실행이 시작된다. 그 앞에서 실패하면
            // beginLongRun이 안 불리고 finish()가 Backdrop을 걷으며 원상복구한다.
            beginLongRun();
            return fpSimulationService.executePlan(execParams, planVersion);
          })
          .then(() => showSnack(snackBarMessageColor.saveSuccess, t("FP_MSG_COMPLETE_EXEC")));
      })
      .catch(fail)
      .then(finish);
  };

  // ── 이벤트 ───────────────────────────────────────────────────────────

  const openExecConfirm = (node, options) => {
    pendingExecRef.current = { node, options };
    setDialogInfo({ open: true, title: "EXEC", content: t("FP_MSG_EXEC_SIMULATION") });
  };

  const onExecClick = (node) => {
    detailGridRef.current?.api?.stopEditing();

    // old validateCells: 계획 실행 행은 시나리오가 필수다.
    if (node?.data?.execTpCd === EXEC_TP_PLAN && isBlank(node.data.policyCd)) {
      showSnack(snackBarMessageColor.inputError, t("MSG_PleaseEnterValues", { values: t("SCENARIO") }));
      return;
    }

    openExecConfirm(node, null);
  };

  const onOptionClick = (node) => {
    detailGridRef.current?.api?.stopEditing();

    if (node?.data?.execTpCd === EXEC_TP_PLAN && isBlank(node.data.policyCd)) {
      showSnack(snackBarMessageColor.inputError, t("MSG_PleaseEnterValues", { values: t("SCENARIO") }));
      return;
    }

    pendingExecRef.current = { node, options: null };
    setOptionPopup({
      open: true,
      title: `(${stepLabel} : ${node.data.stepSeq}. ${node.data.stepDescTxt ?? ""})`,
      versionCd: null
    });
  };

  const onHistoryOptionClick = (row) => {
    if (isBlank(row?.versionCd)) return;
    setOptionPopup({ open: true, title: `(${row.versionCd})`, versionCd: row.versionCd });
  };

  const onOptionSubmit = (options, validationMessage) => {
    if (validationMessage) {
      showSnack(snackBarMessageColor.inputError, validationMessage);
      return;
    }

    setOptionPopup({ open: false, title: "", versionCd: null });
    openExecConfirm(pendingExecRef.current?.node, options);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      // 절차 마스터에 없는 값이 저장되면 안 된다 — tb_fp_main_version.step_cd에 FK가 없어 DB가 안 막고,
      // old도 검증이 없어(VersionCreation.jsx:284 rules 주석) dangling 코드가 그대로 저장됐다.
      // 조회 시점에 폼 값을 "" 아니면 마스터의 유효 코드로 정리하므로 공백 검사로 충분하다.
      if (isBlank(versionForm.stepCd)) {
        showSnack(snackBarMessageColor.inputError, t("MSG_PleaseEnterValues", { values: t("FP_SIMULATION_STEP") }));
        return;
      }
      setProgressOn(true);
      saveMainVersion();
      return;
    }

    if (action === "EXEC") {
      setProgressOn(true);
      const pending = pendingExecRef.current;
      pendingExecRef.current = null;
      executeStep(pending?.node, pending?.options);
    }
  };

  // ── 컬럼 정의 ────────────────────────────────────────────────────────

  const detailColumnDefs = useMemo(() => ([
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, editable: false },
    { headerName: "seq", field: "stepSeq", colId: "stepSeq", type: ["centerAligned"], width: 80, editable: false },
    { headerName: "FP_PLAN_STEP", field: "stepCd", colId: "stepCd", hide: true, editable: false },
    { headerName: "FP_EXEC_TP_CD", field: "execTpCd", colId: "execTpCd", hide: true, editable: false },
    { headerName: "FP_SIMUL_STEP_DESC", field: "stepDescTxt", colId: "stepDescTxt", width: 280, editable: false },
    { headerName: "status", field: "statusTpCd", colId: "statusTpCd", width: 140, editable: false,
      cellRenderer: StatusRenderer, cellRendererParams: { t } },
    { headerName: "FP_ACTION", field: "action", colId: "action", width: 170, editable: false, sortable: false,
      cellRenderer: ExecActionRenderer,
      cellRendererParams: { t, onExec: onExecClick, onOption: onOptionClick, disabled: engineRunning } },
    { headerName: "FP_FINAL_SIMULATION_VERSION", field: "versionCd", colId: "versionCd", width: 220, editable: false },
    // 시나리오는 old와 동일하게 전 행에서 편집 가능(계획 실행 행에서만 필수 검증이 걸린다).
    // editable을 명시하지 않으면 아래 defaultColDef의 editable:false를 상속해 드롭다운이 아예 안 열린다.
    { headerName: "SCENARIO", field: "policyCd", colId: "policyCd", width: 190, cellClass: "stringType",
      editable: true,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => ({
        values: policyListRef.current.map(policy => policy.policyCd),
        cellRenderer: (params) => policyListRef.current.find(policy => policy.policyCd === params?.value)?.policyNm ?? (params?.value ?? "")
      }),
      // 셀(비편집 상태)에는 코드가 아니라 시나리오명을 보여준다(old lookupDisplay 대응).
      valueFormatter: (params) => policyListRef.current.find(policy => policy.policyCd === params.value)?.policyNm ?? (params.value ?? "") },
    // 설명은 계획 실행 행만 편집 가능(old styleCallback의 editable 토글).
    { headerName: "descTxt", field: "versionDescTxt", colId: "versionDescTxt", width: 260, cellClass: "stringType",
      editable: params => params.data?.execTpCd === EXEC_TP_PLAN },
    { headerName: "startTs", field: "startTs", colId: "startTs", type: ["centerAligned"], width: 150, editable: false,
      valueFormatter: dateUtils.formatDateTime },
    // 대기/진행중인 행은 종료 일시를 비운다(old endTs valueCallback).
    { headerName: "endTs", field: "endTs", colId: "endTs", type: ["centerAligned"], width: 150, editable: false,
      valueFormatter: params => ([STATUS_READY, STATUS_RUNNING].includes(params.data?.statusTpCd) ? "" : dateUtils.formatDateTime(params)) },
    { headerName: "FP_ELAPSED_TIME", field: "elapsedTime", colId: "elapsedTime", type: ["centerAligned"], width: 120,
      editable: false, valueGetter: elapsedTimeGetter }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [t, engineRunning, stepLabel]);

  // TODO(MIG-T2): [simulation/Simulation] 이력 메인 버전 컬럼 셀 병합 미이관.
  //   old: mainVersionCd 컬럼에 RealGrid mergeRule { criteria: "value" }로 같은 값 셀을 세로 병합.
  //   사유: 셀 병합은 saas-fp/docs/fp-frontend-migration/CLAUDE.md Tier 규칙의 명시적 Tier2 항목. 값은 행마다 그대로 표시된다.
  // TODO(MIG-T2): [simulation/Simulation] 실행 로그 상세 팝업(LogPopup) 미이관.
  //   old: statusLog 컬럼용 "logButton" 렌더러를 registerCustomRenderer로 등록만 하고 어떤 컬럼에도
  //        연결하지 않아(setColumnProperty 호출 없음) 실제로는 버튼이 그려지지 않는 죽은 코드였다.
  //   사유: old에서도 동작하지 않던 경로. statusLog 텍스트 자체는 동일하게 표시된다.
  const historyColumnDefs = useMemo(() => ([
    { headerName: "mainVersion", field: "mainVersionCd", colId: "mainVersionCd", type: ["centerAligned"], width: 220, editable: false },
    { headerName: "SIMULATION_VERSION", field: "versionCd", colId: "versionCd", type: ["centerAligned"], width: 240, editable: false },
    { headerName: "descTxt", field: "descTxt", colId: "descTxt", width: 260, editable: false },
    { headerName: "SCENARIO", field: "policyNm", colId: "policyNm", width: 220, editable: false },
    { headerName: "option", field: "hasOptions", colId: "hasOptions", type: ["centerAligned"], width: 90, editable: false,
      sortable: false, cellRenderer: OptionIconRenderer, cellRendererParams: { onOpen: onHistoryOptionClick } },
    { headerName: "confirm", field: "confirmYn", colId: "confirmYn", type: ["centerAligned"], width: 90, editable: false,
      cellRenderer: ConfirmRenderer },
    { headerName: "startTs", field: "startTs", colId: "startTs", type: ["centerAligned"], width: 150, editable: false,
      valueFormatter: dateUtils.formatDateTime },
    { headerName: "endTs", field: "endTs", colId: "endTs", type: ["centerAligned"], width: 150, editable: false,
      valueFormatter: dateUtils.formatDateTime },
    { headerName: "FP_ELAPSED_TIME", field: "elapsedTime", colId: "elapsedTime", type: ["centerAligned"], width: 120,
      editable: false, valueGetter: elapsedTimeGetter },
    { headerName: "FP_DETAILS", field: "statusLog", colId: "statusLog", width: 420, editable: false },
    // 사용자는 id가 아니라 이름으로 — PlanHistoryQueryRepository의 z_user 2-join으로 채워진다(백엔드 리빌드 필요).
    { headerName: "user", field: "modifyUser", colId: "modifyUser", type: ["centerAligned"], width: 130, editable: false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [t]);

  const onDetailGridReady = params => {
    DataState.initialize(params.api);
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: detailColumnDefs, viewName, gridId: "detail", initState: true });
  };

  const onHistoryGridReady = params => {
    DataState.initialize(params.api);
    GridUtils.setColumnDefs({ ...params, columnDefs: historyColumnDefs, viewName, gridId: "history", initState: true });
  };

  // ── 효과 ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!gridLoading || !planScope) return;

    const reqId = ++latestScopeReqRef.current;
    latestDetailReqRef.current += 1;

    setDetails([]);
    setHistories([]);
    setMainVersion(null);

    retrievePlanPolicies(reqId);
    retrievePlanSteps(reqId);
    retrieveHistories(reqId);
    checkEngineStatus();

    const reduxState = reduxUtil.createObjectState(viewName, { planScope });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  useEffect(() => {
    mainVersionRef.current = mainVersion;
    retrieveDetails(mainVersion?.mainVersionCd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainVersion]);

  // 언어 전환/실행중 상태 변화로 컬럼 정의가 새로 만들어지면 그리드에 다시 적용한다.
  useEffect(() => {
    const api = detailGridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    GridUtils.setColumnDefs({ api, columnDefs: detailColumnDefs, viewName, gridId: "detail" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailColumnDefs]);

  useEffect(() => {
    const api = historyGridRef.current?.api;
    if (!api || api.isDestroyed()) return;
    GridUtils.setColumnDefs({ api, columnDefs: historyColumnDefs, viewName, gridId: "history" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyColumnDefs]);

  useEffect(() => () => stopEnginePolling(), []);

  // ── 렌더 ─────────────────────────────────────────────────────────────

  const panelSx = { display: "flex", flexDirection: "column", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" };
  const panelHeaderSx = { height: PANEL_HEADER_HEIGHT, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, px: 1.5, borderBottom: 1, borderColor: "divider" };

  return (
    <>
      {/* TODO(MIG-T2): [simulation/Simulation] 조회영역 없음 표시(NO_SEARCHAREA) 미이관.
          old: useEffect에서 setViewInfo(activeViewId,'NO_SEARCHAREA',true)로 전역 뷰 정보에 등록.
          사유: new에는 화면별 뷰 정보 레지스트리라는 개념 자체가 없다(Tier3). */}
      <FilterContainer hasIcons={false}>
        <PlanScope value={planScope} onChange={setPlanScope} />
      </FilterContainer>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1, minHeight: 0, mt: 1, mb: 1, overflow: "auto" }}>
        <Box sx={{ display: "flex", gap: 1.5, flex: "0 0 auto", height: TOP_AREA_HEIGHT }}>
          {/* 좌: 메인 버전 생성 */}
          <Box sx={{ ...panelSx, width: VERSION_PANEL_WIDTH, flex: "0 0 auto" }}>
            <Box sx={panelHeaderSx}>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>{mainVersionCd}</Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <VersionField label={t("descTxt")}>
                  <TextField
                    fullWidth
                    variant="standard"
                    placeholder={t("FP_MSG_ENTER_DESCRIP")}
                    disabled={versionReadOnly}
                    value={versionForm.descTxt}
                    onChange={event => setVersionForm(prev => ({ ...prev, descTxt: event.target.value }))}
                    sx={{ maxWidth: VERSION_INPUT_WIDTH }}
                  />
                </VersionField>

                <VersionField label={t("planHorizon")}>
                  <DateTimePicker
                    format="yyyy-MM-dd HH:mm"
                    disabled={versionReadOnly}
                    value={versionForm.startTs}
                    maxDateTime={versionForm.endTs ?? undefined}
                    onChange={value => setVersionForm(prev => ({ ...prev, startTs: value }))}
                    slotProps={{ textField: { variant: "standard", sx: { width: VERSION_INPUT_WIDTH } } }}
                  />
                  <Typography variant="body2" color="text.secondary">-</Typography>
                  <DateTimePicker
                    format="yyyy-MM-dd HH:mm"
                    disabled={versionReadOnly}
                    value={versionForm.endTs}
                    minDateTime={versionForm.startTs ?? undefined}
                    onChange={value => setVersionForm(prev => ({ ...prev, endTs: value }))}
                    slotProps={{ textField: { variant: "standard", sx: { width: VERSION_INPUT_WIDTH } } }}
                  />
                </VersionField>

                {/* 동결 구간은 계획 구간 안에 있어야 한다(old filterTimeForFreeze). */}
                <VersionField label={t("FP_FREEZE_HORIZON")}>
                  <DateTimePicker
                    format="yyyy-MM-dd HH:mm"
                    disabled={versionReadOnly}
                    value={versionForm.freezeTs}
                    minDateTime={versionForm.startTs ?? undefined}
                    maxDateTime={versionForm.endTs ?? undefined}
                    onChange={value => setVersionForm(prev => ({ ...prev, freezeTs: value }))}
                    slotProps={{ textField: { variant: "standard", sx: { width: VERSION_INPUT_WIDTH } } }}
                  />
                </VersionField>

                <VersionField label={t("FP_SIMULATION_STEP")} last>
                  <TextField
                    select
                    variant="standard"
                    disabled={versionReadOnly}
                    value={versionForm.stepCd}
                    onChange={event => setVersionForm(prev => ({ ...prev, stepCd: event.target.value }))}
                    sx={{ width: VERSION_INPUT_WIDTH }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        const step = isBlank(selected) ? null : stepList.find(s => s.stepCd === selected);
                        if (step) return step.stepNm;
                        // 절차 마스터에 없는 값은 내부 코드를 노출하지 않고 미선택과 동일하게 안내한다
                        // (old 공용 SelectControl이 options 밖 값을 ''로 치환하던 것과 같은 취지 —
                        //  wingui-core RegistInputField.jsx:182). 폼 값은 조회 시점에 이미 정리되지만,
                        // 저장된 버전의 절차가 나중에 삭제된 경우를 위해 여기서도 막는다.
                        return <Typography variant="body2" color="text.disabled" noWrap>{t("FP_MSG_SELECT_SIMULATION_STEP")}</Typography>;
                      }
                    }}
                  >
                    {stepList.map(step => (
                      <MenuItem key={step.stepCd} value={step.stepCd}>{step.stepNm}</MenuItem>
                    ))}
                  </TextField>
                </VersionField>
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
              {/* 실행 중에는 좌측 메인 버전 패널도 잠근다. old는 waitMe로 작업영역 전체를 덮었고,
                  new는 Backdrop을 걷은 대신 이 버튼들을 막는다 — 실행 중에 메인 버전을 새로 만들면
                  상세 그리드가 통째로 갈려 사용자가 실행 결과를 놓친다. */}
              <Button variant="outlined" startIcon={<AddIcon />} disabled={engineRunning} onClick={createNewMainVersion} sx={{ borderRadius: 5 }}>
                {t("FP_MAIN_VERSION_CREATION")}
              </Button>
              <Button
                variant="contained"
                disabled={engineRunning || versionReadOnly || isBlank(mainVersionCd)}
                onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })}
                sx={{ ml: "auto", minWidth: 120 }}
              >
                {t("save")}
              </Button>
            </Box>
          </Box>

          {/* 우: 실행 절차 상세 */}
          <Box sx={{ ...panelSx, flex: 1, minWidth: 0 }}>
            <Box sx={panelHeaderSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{stepLabel}</Typography>
              {engineRunning && (
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress color="info" size={16} />
                  <Typography variant="caption" color="text.secondary">{t("FP_STATUS_RUNNING")}</Typography>
                </Box>
              )}
            </Box>
            <Box id="fp-simulation-detail-grid" className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
              {/* TODO(MIG-T2): [simulation/Simulation] 컬럼 자동필터 미이관.
                  old: setNoneEditableGrid의 setFilteringOptions로 그리드 헤더 필터 UI 사용.
                  사유: 컬럼 필터는 Tier2(1차 유보) 표준 항목 — FP 다른 화면과 동일하게 미적용. */}
              <AgGridReact
                ref={detailGridRef}
                rowData={details}
                {...detailGridMemo}
                defaultColDef={{ ...detailGridMemo.defaultColDef, editable: false }}
                // fp-frontend.md §0-4 "편집 그리드는 singleClickEdit". 공용 DefaultGridSetting엔 없어
                // 그리드 단위로 켠다(DP의 BfReview 등과 같은 방식). 시나리오 드롭다운을 한 번 클릭으로 연다.
                // 이력 그리드는 편집 컬럼이 없어 적용하지 않는다.
                singleClickEdit
                onGridReady={onDetailGridReady}
                onCellValueChanged={detailGridMemo.onCellValueChanged}
              />
            </Box>
          </Box>
        </Box>

        {/* 하단: 실행 이력 */}
        <Box sx={{ ...panelSx, flex: 1, minHeight: HISTORY_MIN_HEIGHT }}>
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("FP_SIMULATION_HISTORY")}</Typography>
            <Box sx={{ ml: "auto" }}>
              <Tooltip title={t("search")}>
                <IconButton aria-label="refreshHistory" color="info" size="small" onClick={() => retrieveHistories()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box id="fp-simulation-history-grid" className="ag-theme-balham" sx={{ flex: 1, minHeight: 0 }}>
            <AgGridReact
              ref={historyGridRef}
              rowData={histories}
              {...historyGridMemo}
              defaultColDef={{ ...historyGridMemo.defaultColDef, editable: false }}
              onGridReady={onHistoryGridReady}
            />
          </Box>
        </Box>
      </Box>

      <SimulationOptionPopup
        open={optionPopup.open}
        onClose={() => setOptionPopup({ open: false, title: "", versionCd: null })}
        planScope={planScope}
        versionCd={optionPopup.versionCd}
        title={optionPopup.title}
        onSubmit={onOptionSubmit}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: "#fff", zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(Simulation);
